<!--
© Copyright 2015-2026 Personalized Privacy Assistant Project, Carnegie Mellon University - All Rights Reserved - US Patent 11,768,949, US Patent 10,956,586, US Patent 12,235,973, and additional USPTO and EPO patents pending.
This code is proprietary and confidential. For any questions about this code, including licensing, please contact Prof. Norman Sadeh (sadeh@cs.cmu.edu).
-->
<template>
  <div>
    <form id="publish-request-form" method="POST" action="">
      <input type="hidden" name="_token" :value="csrf_token">
      <input type="hidden" id="rejection_reason" name="rejection_reason" :value="rejectionReason">
      <input v-if="showAllRegistries" type="hidden" name="return_to_all" value="1">
    <div class="container-fluid">
      <div class="card bg-light mb-3">
        <center>
          <h2 v-if="showAllRegistries" class="card-header">Pending Resource Publication Requests</h2>
          <template v-else>
            <h2 class="card-header">Resource Publication Requests - {{ irr.name }}</h2>
            <p><small>ID # {{ irr.id }}</small></p>
          </template>
        </center>
        <div class="card-body">
          <div class="card-text">
            <div class="card">
              <table class="table table-sm table-hover table-borderless">
                <thead class="border-bottom">
                  <tr>
                    <th class="larger pl-3" scope="col">Resource</th>
                    <th v-if="showAllRegistries" class="larger" scope="col">Registry</th>
                    <th class="larger" scope="col">Status</th>
                    <th class="larger" scope="col">Requested by</th>
                    <th class="larger" scope="col">&nbsp;</th> <!-- Column for button to publish -->
                    <th class="larger" scope="col">&nbsp;</th> <!-- Column for button to decline -->
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="publishRequest in publishRequests">
                    <th class="pl-3"><a :href="'/resource/' + publishRequest.resource_id + '/'">{{publishRequest.resource_display_name}}</a></th>
                    <th v-if="showAllRegistries"><a :href="'/irr/' + publishRequest.irr_id + '/'">{{publishRequest.irr_display_name}}</a></th>
                    <th>{{requestStatus(publishRequest)}}</th>
                    <!-- <th><a :href="'/user/' + publishRequest.user_id + '/'">{{publishRequest.user_display_name}}</a></th> -->
                    <th>{{publishRequest.user_display_name}}</th>

                    <!-- If an ADMIN, and status is PENDING, show buttons for approval/denial -->
                    <th v-if="publishRequest.pending && canApprove"><a href="#" class="btn btn-registry btn-sm" role="button" v-on:click.prevent="showApproveModal(publishRequest)">Publish Resource</a></th>
                    <th v-else>&nbsp;</th>
                    <th v-if="publishRequest.pending && canApprove"><a href="#" class="btn btn-danger btn-sm" role="button" v-on:click.prevent="showDeclineModal(publishRequest)">Decline Request</a></th>
                    <th v-else>&nbsp;</th>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    </form>

    <!-- Decline Publish Request Modal -->
    <div class="modal fade" id="declinePublishModal" tabindex="-1" role="dialog" aria-labelledby="declinePublishModalLabel" aria-hidden="true" style="z-index: 10001;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="declinePublishModalLabel">Decline Publication Request</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p>Please provide a reason for declining this resource publication request. This reason will be sent to the requestor via email.</p>
            <textarea v-model="rejectionReason" class="form-control" rows="5" placeholder="Enter decline reason here..." maxlength="1000"></textarea>
            <small class="form-text text-muted mt-2">{{ rejectionReason.length }}/1000 characters</small>
            <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-registry" @click="submitDecline" :disabled="!rejectionReason.trim()">Decline Request</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Approve Publish Modal -->
    <div class="modal fade" id="approvePublishModal" tabindex="-1" role="dialog" aria-labelledby="approvePublishModalLabel" aria-hidden="true" style="z-index: 10001;">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="approvePublishModalLabel">Approve Publication Request</h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            By approving the publication of this resoruce in this registry, you agree to take responsibility for the content of this resource description. In particular, you represent that the description provided in this resource is accurate to the best of your knowledge and confirms to all applicable laws.
            <br>&nbsp;<br>
            <div class="checkbox">
              <label><input id="approvePublishCheckbox" type="checkbox" value="" onchange="(!this.checked) ? $('#modal-approve-publish-button').addClass('disabled') : $('#modal-approve-publish-button').removeClass('disabled');">&nbsp;<strong>I have read, understood and agreed to the above.</strong></label>
            </div>
            <button type="button" class="btn btn-danger" data-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-registry disabled" role="button" id="modal-approve-publish-button" @click="submitApprove">Approve</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
    export default {
      props: {
        route: String,
        permissions: Object,
        irr: Object,
        publishRequests: [Object, Array],
        csrf_token: String,
        showAllRegistries: {
          type: Boolean,
          default: false,
        },
      },
      data: function () {
        return {
          approveRequestId: null,
          approveIrrId: null,
          declineRequestId: null,
          declineIrrId: null,
          rejectionReason: '',
        }
      },
      computed: {
        canApprove: function () {
          // Cross-registry page is already scoped to Owner/Admin IRRs server-side.
          if (this.showAllRegistries) {
            return true;
          }
          return this.permissions && this.permissions.currentPermissions
            && this.permissions.currentPermissions.IRR_PUBLISH_RESOURCE;
        },
      },
      methods: {
        requestStatus: function (request) {
          return ( (request.pending) ? 'Pending' : ( (request.approved) ? 'Approved' : 'Declined') );
        },
        requestIrrId: function (publishRequest) {
          return publishRequest.irr_id || (this.irr && this.irr.id);
        },
        showApproveModal: function (publishRequest) {
          this.approveRequestId = publishRequest.id;
          this.approveIrrId = this.requestIrrId(publishRequest);
          $('#approvePublishModal').modal('show');
          this.$nextTick(function () {
            $('#approvePublishCheckbox').prop('checked', false);
            $('#modal-approve-publish-button').addClass('disabled');
          });
        },
        showDeclineModal: function (publishRequest) {
          this.declineRequestId = publishRequest.id;
          this.declineIrrId = this.requestIrrId(publishRequest);
          this.rejectionReason = '';
          $('#declinePublishModal').modal('show');
        },
        submitDecline: function () {
          if (this.rejectionReason.trim() && this.declineRequestId && this.declineIrrId) {
            $('#rejection_reason').val(this.rejectionReason);
            $('#publish-request-form').attr('action', '/irr/' + this.declineIrrId + '/publishrequests/' + this.declineRequestId + '/0');
            $('#declinePublishModal').modal('hide');
            $('#publish-request-form').submit();
          }
        },
        submitApprove: function () {
          if (!this.approveRequestId || !this.approveIrrId || $('#modal-approve-publish-button').hasClass('disabled')) {
            return;
          }
          $('#publish-request-form').attr('action', '/irr/' + this.approveIrrId + '/publishrequests/' + this.approveRequestId + '/1');
          $('#approvePublishModal').modal('hide');
          $('#publish-request-form').submit();
        },
      },
      mounted() {
        //
      }
    }
</script>
